import { useAuth0 } from "@auth0/auth0-react";
import DealBuilderAPI from "../api/deal-builder-api.ts";
import moment, { Moment } from "moment";
import Result from "../utils/result.utils.ts";
import { DealBuilder } from "../services/deal-builder.ts";
import { Bond } from "../pages/debt-service-page.tsx";
import { PrimaryTimeUtils } from "../services/utils/primary-time.utils.ts";
import { BuilderOutput, ProductionUtils } from "../services/utils/production.utils.ts";

const BACKEND_FETCH_ERROR_MSG = "We couldn't connect to our backend.";

export interface SpreadPrediction {
  yieldCurve: number[];
  spreads: number[];
  coupons: number[];
}

export default function useDealBuilderAPI() {
  const { getAccessTokenSilently } = useAuth0();

  /**
   * Transforms a iso date into a unix timestamp in seconds
   */
  function dateStrToUnixSeconds(date: string | Moment): number {
    if (typeof date === "string") {
      return Math.floor(moment(date).valueOf() / 1000)
    }
    return date.valueOf() / 1000;
  }

  async function fetchCouponsAndSpreads(builder: DealBuilder, bonds: Bond[], predictCoupons: boolean = false): Promise<Result<BuilderOutput>> {
    if (bonds.length === 0) return { valid: true, body: [] };

    const parAmount = bonds.reduce((prev, curr) => (
      prev + ProductionUtils.getBondPrincipal(curr)
    ), 0)

    const token = await getAccessTokenSilently();
    const response = await DealBuilderAPI.getPredictions(token, {
      callDate: dateStrToUnixSeconds(builder.callDate ? builder.callDate : moment("3000-01-01")),
      marketConditionsDate: dateStrToUnixSeconds(builder.marketConditionsDate),
      bondInsurance: builder.bondInsurance,
      parAmount: parAmount,
      saleMethod: builder.saleMethod,
      taxDesignation: builder.taxDesignation,
      stateOfIssuance: builder.stateOfIssuance,
      securityType: builder.securityType,
      ratingAssumption: builder.ratingAssumption,
      couponUserInput: !predictCoupons,

      enhancedRating: builder.stateEnhancement || "",
      stateEnhancement: builder.stateEnhancement !== undefined && builder.stateEnhancement !== null,

      coupon_list: bonds.map(bond => bond.coupon * 100),
      par_amount_list: bonds.map(bond => ProductionUtils.getBondPrincipal(bond)),
      term_bonds_list: bonds.map(bond => bond.type === "term"),
      years_to_maturity_list: bonds.map(bond => (
        PrimaryTimeUtils.daysDifference360(builder.deliveryDate, moment(bond.maturity)) / 360)
      ),
    });

    if ("error" in response) return { valid: false, error: BACKEND_FETCH_ERROR_MSG };
    const result: BuilderOutput = response.data.map((entry, index) => ({
      bond: bonds[index],
      date: bonds[index].maturity,
      curve: entry.AAA / 100,
      spread: entry.spread / 100,
      coupon: entry.coupon / 100,
    }));

    return { valid: true, body: result };
  }

  return { fetchCouponsAndSpreads };
}
